import React from 'react';
import {
    Avatar,
    AvatarSizes,
    AvatarShapes,
    Button,
    ButtonVariants,
    ButtonColors,
    ButtonFontWeights,
    Text,
    TextAs,
    TextSizes,
    Tooltip,
} from '@clockout-dev/design-elements';
import UserHeaderTag from './UserHeaderTag';
import { USER_TAG_TONES } from '../user.constants';
import { SSO_DISABLED_TOOLTIP } from '../../../../constants/sso.constants';
import styles from './styles/user-header.module.scss';

/**
 * Page header for the user-journey detail page (Figma 5747:1683).
 *
 * Composition:
 *  - Leading: large avatar wrapped in a soft-blue halo (87x83), with
 *    initials derived from the user name by the design-elements
 *    `Avatar` itself.
 *  - Centre: heading (40px) with the surname token rendered in the
 *    brand-blue highlight colour, plus a row of meta chips (work
 *    type, payroll system, pay-period range).
 *  - Trailing: two action buttons — `Open SSO` (navy) and
 *    `Restrict Access` (soft red). `Restrict Access` exposes a
 *    `loading` flag so the controller can disable it while the
 *    underlying network call is in flight. `Open SSO` needs none:
 *    it only opens a tab.
 *
 * `Open SSO` renders for every admin and is *disabled* — never
 * hidden — when the admin lacks `admin:sso:open`, with a tooltip
 * saying who can grant it, reachable by hover and by keyboard focus
 * on the wrapper. `Restrict Access` still disappears without its
 * scope: that one is delegable, so an admin who cannot see it can be
 * given it by their own admin without escalating.
 *
 * The header owns a soft bottom border that separates it from the
 * KPI row below — mirrors the `HorizontalBorder` node in Figma.
 *
 * Pre-derived values (`nameParts`, `payPeriod`) come from the
 * controller (`formatUserNameHighlight` / `parsePayPeriod`) so this
 * component stays presentational.
 *
 * @param {Object} props
 * @param {string} props.name Full user name (already trimmed).
 * @param {{ head: string, tail: string }} props.nameParts
 *   Pre-split name from `controller.formatUserNameHighlight`.
 * @param {{ rangeChipLabel: string } | null} [props.payPeriod]
 *   Pre-parsed pay period from `controller.parsePayPeriod`.
 * @param {string | null} [props.workTypeLabel]
 * @param {string | null} [props.payrollSystemLabel]
 * @param {string | null} [props.lastConnectionLabel]
 *   Pre-formatted payroll line from `controller.resolvePayrollHeaderSummary`,
 *   e.g. "08/21/2026 - 03:14:05 PM (12 days ago) · ADP", or the explicit
 *   "Never connected". Null while the summary is loading or unreadable, so the
 *   header stays quiet rather than asserting the member never connected.
 * @param {() => void} props.onOpenSso
 * @param {() => void} props.onToggleRestrict
 * @param {boolean} [props.restrictLoading]
 * @param {boolean} [props.isRestricted]
 * @param {boolean} [props.canRestrict] Gates the Restrict / Unrestrict button.
 * @param {boolean} [props.canOpenSso] Enables Open SSO; false disables it.
 */
function UserHeader({
    name,
    nameParts,
    payPeriod,
    workTypeLabel,
    payrollSystemLabel,
    lastConnectionLabel,
    onOpenSso,
    onToggleRestrict,
    restrictLoading,
    isRestricted,
    canRestrict = true,
    canOpenSso = false,
}) {
    const safeName = name || 'Unknown user';
    const head = nameParts?.head || safeName;
    const tail = nameParts?.tail || '';

    return (
        <header className={styles.header}>
            <div className={styles.identity}>
                <div className={styles.avatarHalo}>
                    <Avatar
                        name={safeName}
                        size={AvatarSizes.LARGE}
                        shape={AvatarShapes.ROUNDED}
                        classNames={{
                            wrapper: styles.avatarWrapper,
                            root: styles.avatarRoot,
                            initials: styles.avatarInitials,
                            label: styles.avatarLabel,
                            title: styles.avatarLabel,
                            subtitle: styles.avatarLabel,
                        }}
                    />
                </div>

                <div className={styles.identityStack}>
                    <Text
                        as={TextAs.H2}
                        size={TextSizes.DISPLAY_SM}
                        className={styles.name}
                    >
                        {head}
                        {tail ? ' ' : ''}
                        {tail ? <span className={styles.nameHighlight}>{tail}</span> : null}
                    </Text>

                    <div className={styles.chips}>
                        {workTypeLabel ? (
                            <UserHeaderTag label={workTypeLabel} tone={USER_TAG_TONES.PRIMARY} />
                        ) : null}
                        {payrollSystemLabel ? (
                            <UserHeaderTag label={payrollSystemLabel} tone={USER_TAG_TONES.NEUTRAL} />
                        ) : null}
                        {payPeriod?.rangeChipLabel ? (
                            <UserHeaderTag label={payPeriod.rangeChipLabel} tone={USER_TAG_TONES.INFO} />
                        ) : null}
                    </div>

                    {lastConnectionLabel ? (
                        <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.lastConnection}>
                            <span className={styles.lastConnectionLabel}>Last payroll connection:</span>{' '}
                            {lastConnectionLabel}
                        </Text>
                    ) : null}
                </div>
            </div>

            <div className={styles.actions}>
                {/* The span is both the Tooltip's anchor and the button's
                    layout slot: a natively-disabled <button> fires no mouse
                    events AND is out of the tab order, so the one control
                    with something to explain could otherwise be neither
                    hovered nor reached. `tabIndex` goes on only when there is
                    something to say — an enabled button is already its own
                    tab stop and the slot must not add a second, dead one. */}
                <Tooltip
                    title={canOpenSso ? '' : SSO_DISABLED_TOOLTIP}
                    trigger={['hover', 'focus']}
                >
                    <span
                        className={styles.actionSlot}
                        data-testid="open-sso-slot"
                        tabIndex={canOpenSso ? undefined : 0}
                    >
                        <Button
                            variant={ButtonVariants.DEFAULT}
                            color={ButtonColors.PRIMARY}
                            fontWeight={ButtonFontWeights.BOLD}
                            onClick={onOpenSso}
                            disabled={!canOpenSso}
                            className={`${styles.actionBtn} ${styles.actionBtnSso}`}
                        >
                            Open SSO
                        </Button>
                    </span>
                </Tooltip>
                {canRestrict ? (
                    <Button
                        variant={ButtonVariants.DEFAULT}
                        color={ButtonColors.LIGHT}
                        fontWeight={ButtonFontWeights.BOLD}
                        onClick={onToggleRestrict}
                        disabled={!!restrictLoading}
                        className={`${styles.actionBtn} ${styles.actionBtnRestrict}`}
                    >
                        {isRestricted ? 'Unrestrict Access' : 'Restrict Access'}
                    </Button>
                ) : null}
            </div>
        </header>
    );
}

export default UserHeader;
